import { useId, useRef, useState, type DragEvent } from 'react';

import { Icon } from '@/components/Icon/Icon';
import { matchesAccept } from '@/lib/accept';
import { formatBytes } from '@/lib/format';
import type { UploadProgress } from '@/lib/upload';

import styles from './FileDropzone.module.css';

export interface FileDropzoneProps {
  label: string;
  file: File | null;
  onChange: (file: File | null) => void;
  /** The `accept` attribute, e.g. `application/pdf,.pdf`. */
  accept?: string;
  /** Says what may be uploaded, under the prompt. */
  hint?: string;
  disabled?: boolean;
  /** Where the upload has got to, once one is running. */
  progress?: UploadProgress | null;
}

const STAGE_LABELS = {
  reserving: 'Getting ready…',
  uploading: 'Uploading…',
  validating: 'Checking the file…',
} as const;

/** Drop a file here or browse for one, then watch it upload. */
export function FileDropzone({
  label,
  file,
  onChange,
  accept,
  hint,
  disabled = false,
  progress = null,
}: FileDropzoneProps) {
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const id = useId();

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (disabled) {
      return;
    }
    const dropped = event.dataTransfer.files[0];
    if (dropped) {
      choose(dropped);
    }
  };

  const choose = (chosen: File | null) => {
    if (chosen && !matchesAccept(chosen, accept)) {
      setRefused(`${chosen.name} can't be uploaded here.${hint ? ` ${hint}.` : ''}`);
      return;
    }
    setRefused(null);
    onChange(chosen);
  };

  const percent =
    progress?.fraction !== null && progress?.fraction !== undefined
      ? Math.round(progress.fraction * 100)
      : null;

  return (
    <div className={styles.field}>
      <span className={styles.label} id={`${id}-label`}>
        {label}
      </span>
      <div
        className={[
          styles.zone,
          dragging ? styles.dragging : '',
          file ? styles.chosen : '',
          disabled ? styles.disabled : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onDragLeave={() => setDragging(false)}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) {
            setDragging(true);
          }
        }}
        onDrop={onDrop}
      >
        <input
          ref={input}
          accept={accept}
          aria-labelledby={`${id}-label`}
          className={styles.input}
          disabled={disabled}
          id={id}
          tabIndex={-1}
          onChange={(event) => {
            choose(event.target.files?.[0] ?? null);
            event.target.value = '';
          }}
          type="file"
        />
        {file ? (
          <div className={styles.file}>
            <span className={styles.fileIcon}>
              <Icon name="file-pdf" size="1.4rem" />
            </span>
            <span className={styles.fileText}>
              <span className={styles.fileName}>{file.name}</span>
              <span className={styles.fileMeta}>
                {progress ? STAGE_LABELS[progress.stage] : formatBytes(file.size)}
                {percent !== null ? ` ${percent}%` : null}
              </span>
            </span>
            {!disabled ? (
              <button
                className={styles.change}
                onClick={() => input.current?.click()}
                type="button"
              >
                Choose another
              </button>
            ) : null}
          </div>
        ) : (
          <button
            className={styles.prompt}
            disabled={disabled}
            onClick={() => input.current?.click()}
            type="button"
          >
            <span className={styles.promptIcon}>
              <Icon name="upload-simple" size="1.4rem" />
            </span>
            <span className={styles.promptText}>
              <strong>Choose a file</strong> or drag it here
            </span>
            {hint ? <span className={styles.hint}>{hint}</span> : null}
          </button>
        )}
        {progress ? (
          <div
            aria-label="Upload progress"
            aria-valuemax={100}
            aria-valuemin={0}
            aria-valuenow={percent ?? undefined}
            className={styles.bar}
            role="progressbar"
          >
            <span
              className={`${styles.barFill} ${percent === null ? styles.indeterminate : ''}`}
              style={percent === null ? undefined : { width: `${percent}%` }}
            />
          </div>
        ) : null}
      </div>
      {refused ? (
        <p className={styles.refused} role="alert">
          {refused}
        </p>
      ) : null}
    </div>
  );
}
